import {
  Box,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import type { ManifestTabDto, MonthlyExtractManifestDto } from "src/features/things/monthlyExtractApi"
import { fmtNum } from "../reservationSync/ReservationSyncChrome"

const cell = (v: unknown): string =>
  v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v)

/**
 * LOCKER-469 DoD2 — one tab per workbook sheet, showing the first rows the
 * manifest stored (N2: no xlsx is parsed in the browser).
 *
 * LOCKER-496: a run holds R1, R2 and one R3 per provider, each with its own
 * tabs, so the preview picks a workbook first, then one of its tabs.
 */
export const TabPreview: FC<{ manifest: MonthlyExtractManifestDto }> = ({ manifest }) => {
  const [workbook, setWorkbook] = useState(0)
  const [index, setIndex] = useState(0)
  const wbIndex = Math.min(workbook, manifest.workbooks.length - 1)
  const tabs: ManifestTabDto[] = manifest.workbooks[wbIndex]?.tabs ?? []
  const tab = tabs[Math.min(index, tabs.length - 1)]

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-ui="UI-SL2W-280">
      <Typography sx={{ fontWeight: 600 }}>Preview</Typography>
      {manifest.workbooks.length > 1 && (
        <TextField
          select
          size="small"
          label="Workbook"
          value={wbIndex}
          onChange={(e) => {
            setWorkbook(Number(e.target.value))
            setIndex(0)
          }}
          sx={{ my: 1, minWidth: 360, maxWidth: "100%" }}
          data-testid="tab-preview-workbook"
        >
          {manifest.workbooks.map((w, i) => (
            <MenuItem key={w.key} value={i} sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
              {w.filename}
            </MenuItem>
          ))}
        </TextField>
      )}
      {!tab ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          The manifest lists no sheets.
        </Typography>
      ) : (
        <>
          <Tabs
            value={Math.min(index, tabs.length - 1)}
            onChange={(_e, v) => setIndex(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((t) => (
              <Tab key={t.name} label={`${t.name} (${fmtNum(t.rowCount)})`} sx={{ textTransform: "none" }} />
            ))}
          </Tabs>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", my: 1 }}>
            {tab.complete
              ? `All ${fmtNum(tab.rowCount)} rows, as written to the workbook.`
              : `First ${fmtNum(tab.preview.length)} of ${fmtNum(tab.rowCount)} rows, as written to the workbook.`}
          </Typography>
          {!tab.preview.length ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              This sheet has no rows.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto", maxHeight: 520 }}>
              <Table size="small" stickyHeader data-testid="tab-preview-table">
                <TableHead>
                  <TableRow>
                    {tab.columns.map((c) => (
                      <TableCell key={c} sx={{ whiteSpace: "nowrap", fontSize: "0.72rem" }}>
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tab.preview.map((row, i) => (
                    <TableRow key={i}>
                      {tab.columns.map((c) => (
                        <TableCell
                          key={c}
                          // LOCKER-505: a complete tab is prose (Documentation) — wrap it.
                          sx={
                            tab.complete
                              ? { whiteSpace: "normal", minWidth: 160, maxWidth: 420, verticalAlign: "top", fontSize: "0.72rem" }
                              : { whiteSpace: "nowrap", fontSize: "0.72rem" }
                          }
                        >
                          {cell(row[c])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}
    </Paper>
  )
}
